import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, Award, Users, BookOpen, Globe, GraduationCap,
  ArrowRight, Play, Star, Zap, Trophy, Rocket,
  BarChart3, Cloud, GitBranch, Code2, Database, Shield, Brain, TrendingUp, Target, CheckCircle, Link2, Bot, Quote, CheckCircle2
} from 'lucide-react';
import HeroFloatingLogos from '../components/HeroFloatingLogos';
import FloatingBadge from '../components/three/FloatingBadge';
import CourseCard from '../components/CourseCard';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';

const ICON_MAP = { BarChart3, Cloud, GitBranch, Code2, Database, Shield, Brain, TrendingUp, Target, CheckCircle2, Link2, Bot, Users, Globe, BookOpen, GraduationCap, CheckCircle };

const STATS = [
  { label: 'Learners', value: '1M+', icon: 'Users' },
  { label: 'Countries', value: '150+', icon: 'Globe' },
  { label: 'Courses', value: '200+', icon: 'BookOpen' },
  { label: 'Instructors', value: '500+', icon: 'GraduationCap' },
];

const HIRING_COMPANIES = ['Google', 'Microsoft', 'Amazon', 'Meta', 'Apple', 'Netflix', 'Adobe', 'IBM', 'Oracle', 'Cisco', 'Intel', 'Salesforce', 'PayPal', 'Uber', 'Airbnb'];

const CAREER_PATHS = [
  { title: 'Data Scientist', avgSalary: '$19K/yr', growth: '+35%', color: 'purple' },
  { title: 'Cloud Architect', avgSalary: '$23K/yr', growth: '+40%', color: 'orange' },
  { title: 'DevOps Engineer', avgSalary: '$17K/yr', growth: '+30%', color: 'blue' },
  { title: 'ML Engineer', avgSalary: '$21K/yr', growth: '+45%', color: 'fuchsia' },
  { title: 'Full Stack Dev', avgSalary: '$15K/yr', growth: '+25%', color: 'emerald' },
  { title: 'Security Analyst', avgSalary: '$16K/yr', growth: '+38%', color: 'red' },
];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState({ heroTitle: 'Master new skills.', heroTagline: 'Ignite your future.', heroSubtitle: '' });

  useEffect(() => {
    api.categories().then(setCategories).catch(() => {});
    api.courses().then(setCourses).catch(() => {});
    api.testimonials().then(setTestimonials).catch(() => {});
    api.settings().then(setSettings).catch(() => {});
  }, []);

  const featured = courses.slice(0, 8);

  return (
    <div className="pt-16">
      <section className="relative overflow-hidden mesh-bg">
        <div className="absolute inset-0 dot-pattern opacity-40 pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-gradient-to-br from-[#6b47dc]/25 to-transparent rounded-full blur-3xl blob" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-gradient-to-br from-[#ff5722]/25 to-transparent rounded-full blur-3xl blob" />

        <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-8 items-center relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur border border-purple-200 text-[#6b47dc] font-semibold text-sm mb-6 shadow-sm">
              <Sparkles size={14} className="text-[#ff5722]" /> #1 Live Learning Platform • 1M+ Learners
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05] mb-5 text-balance">
              {settings.heroTitle}<br />
              <span className="gradient-text">{settings.heroTagline}</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-xl leading-relaxed">{settings.heroSubtitle}</p>
            <div className="flex flex-wrap gap-3 mb-8">
              <Link to="/courses">
                <Button size="lg" className="bg-[#ff5722] hover:bg-[#e64a19] text-white font-bold shadow-lg hover:shadow-xl btn-shine px-7 h-12">
                  Explore Courses <ArrowRight size={18} className="ml-1" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-2 border-[#6b47dc] text-[#6b47dc] hover:bg-[#6b47dc] hover:text-white font-bold h-12 px-6">
                <Play size={16} className="mr-2 fill-current" /> Watch demo
              </Button>
            </div>
            <div className="grid grid-cols-4 gap-2 max-w-lg">
              {STATS.map((s) => {
                const Icon = ICON_MAP[s.icon];
                return (
                  <div key={s.label} className="bg-white/80 backdrop-blur border border-purple-100 rounded-xl p-3 text-center">
                    <Icon size={18} className="mx-auto text-[#6b47dc] mb-1" />
                    <div className="font-extrabold text-lg">{s.value}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">{s.label}</div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <div className="relative h-[420px] md:h-[520px] lg:h-[560px]">
            <HeroFloatingLogos />
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/90 backdrop-blur px-4 py-2 rounded-full border border-purple-100 shadow-lg text-xs font-semibold text-gray-700">
              <Zap size={13} className="text-[#ff5722]" /> Live tech stack • hover any logo
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 border-y border-gray-100 bg-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <p className="text-center text-xs font-bold uppercase tracking-widest text-gray-500 mb-6">Our learners work at world-class companies</p>
          <div className="relative overflow-hidden">
            <div className="flex gap-12 marquee whitespace-nowrap w-max">
              {[...HIRING_COMPANIES, ...HIRING_COMPANIES].map((c, i) => (
                <div key={i} className="font-extrabold text-2xl text-gray-400 hover:text-[#6b47dc] transition-colors">{c}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="text-sm font-bold uppercase tracking-widest text-[#ff5722] mb-2">Explore</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Learn from top categories</h2>
            </div>
            <Link to="/courses" className="text-[#6b47dc] font-semibold hover:underline flex items-center gap-1">View all <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((cat, i) => {
              const Icon = ICON_MAP[cat.icon] || BookOpen;
              return (
                <motion.div key={cat.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}>
                  <Link to={`/courses?category=${cat.id}`} className="group block">
                    <div className="bg-white p-5 rounded-2xl border border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all hover:-translate-y-1 h-full">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-orange-100 grid place-items-center mb-3 group-hover:from-[#6b47dc] group-hover:to-[#ff5722] transition-colors">
                        <Icon size={22} className="text-[#6b47dc] group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-bold text-gray-900 group-hover:text-[#6b47dc] transition-colors">{cat.name}</h3>
                      <p className="text-xs text-gray-500 mt-1">{cat.count} courses</p>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-white to-purple-50/30">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="text-sm font-bold uppercase tracking-widest text-[#ff5722] mb-2">Most Popular</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Bestselling courses</h2>
              <p className="text-gray-600 mt-2">Handpicked programs loved by thousands of learners</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {featured.map(c => <CourseCard key={c.id} course={c} />)}
          </div>
          <div className="text-center mt-10">
            <Link to="/courses">
              <Button size="lg" className="bg-[#6b47dc] hover:bg-[#5a3ac0] text-white font-bold px-8 h-12 btn-shine shadow-lg">
                Browse all courses <ArrowRight size={18} className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <p className="text-sm font-bold uppercase tracking-widest text-[#ff5722] mb-3">Why Skillbridge.guru</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">A learning experience like no other</h2>
            <p className="text-gray-600 mb-8 text-lg">We combine world-class instructors, cutting-edge curriculum and dedicated 24/7 support to deliver measurable outcomes.</p>
            <div className="space-y-4">
              {[
                { icon: Trophy, title: 'Industry-recognized certificates', desc: 'Certifications co-branded with top universities and industry leaders.' },
                { icon: Rocket, title: 'Live instructor-led sessions', desc: 'Interactive classes with 24/7 doubt clearing support.' },
                { icon: Award, title: 'Placement assistance', desc: 'Career coach, resume prep and 3,000+ hiring partners.' },
                { icon: Zap, title: 'Real-world capstone projects', desc: 'Hands-on projects with real datasets from industry.' },
              ].map((f) => (
                <div key={f.title} className="flex gap-4 items-start p-4 bg-white rounded-xl border border-gray-100 hover:border-purple-200 hover:shadow-md transition-all">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#6b47dc] to-[#ff5722] grid place-items-center text-white flex-shrink-0">
                    <f.icon size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{f.title}</h4>
                    <p className="text-sm text-gray-600 mt-0.5">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-2xl border border-purple-100 shadow-lg mt-8">
                <FloatingBadge color="#6b47dc" height="180px" />
                <div className="font-bold text-center text-gray-900">Trusted Learning</div>
                <p className="text-xs text-gray-500 text-center mt-1">4.7/5 avg rating</p>
              </div>
              <div className="bg-white p-5 rounded-2xl border border-orange-100 shadow-lg">
                <FloatingBadge color="#ff5722" height="180px" />
                <div className="font-bold text-center text-gray-900">Career Outcomes</div>
                <p className="text-xs text-gray-500 text-center mt-1">72% salary hike</p>
              </div>
              <div className="bg-white p-5 rounded-2xl border border-purple-100 shadow-lg">
                <FloatingBadge color="#a855f7" height="180px" />
                <div className="font-bold text-center text-gray-900">Global Community</div>
                <p className="text-xs text-gray-500 text-center mt-1">150+ countries</p>
              </div>
              <div className="bg-white p-5 rounded-2xl border border-orange-100 shadow-lg mt-8">
                <FloatingBadge color="#facc15" height="180px" />
                <div className="font-bold text-center text-gray-900">Live Support</div>
                <p className="text-xs text-gray-500 text-center mt-1">24/7 mentorship</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-bold uppercase tracking-widest text-[#ff5722] mb-2">Career Growth</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">In-demand career paths</h2>
            <p className="text-gray-600 mt-2 max-w-xl mx-auto">Discover the roles that our learners transition into after completing our programs.</p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {CAREER_PATHS.map(c => (
              <div key={c.title} className="group p-5 rounded-2xl border border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all bg-white text-center hover:-translate-y-1">
                <div className={`w-12 h-12 mx-auto rounded-full bg-${c.color}-100 grid place-items-center mb-3 group-hover:scale-110 transition-transform`}>
                  <TrendingUp size={20} className={`text-${c.color}-600`} />
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">{c.title}</h4>
                <div className="text-xs text-gray-500">Avg: <span className="font-bold text-gray-900">{c.avgSalary}</span></div>
                <div className="mt-2 text-[11px] font-bold text-emerald-600">{c.growth} demand</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-purple-50/40 to-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-bold uppercase tracking-widest text-[#ff5722] mb-2">Testimonials</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Learners love their transformation</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map(t => (
              <div key={t.id} className="bg-white p-6 rounded-2xl border border-purple-100 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 relative">
                <Quote size={28} className="absolute top-4 right-4 text-purple-100" />
                <div className="flex gap-1 mb-3">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} size={14} className="fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-5">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <div className="font-bold text-sm text-gray-900">{t.name}</div>
                    <div className="text-xs text-gray-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#6b47dc] via-[#7c3aed] to-[#ff5722] p-10 md:p-16 text-center text-white">
            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
            <div className="relative">
              <Sparkles size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl md:text-5xl font-extrabold mb-4">Ready to level up your career?</h2>
              <p className="text-lg md:text-xl opacity-90 mb-8 max-w-2xl mx-auto">Join over 1 million learners across 150+ countries advancing their careers with Skillbridge.guru.</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/signup"><Button size="lg" className="bg-white text-[#6b47dc] hover:bg-gray-100 font-bold h-12 px-8 shadow-xl">Start learning free <ArrowRight size={18} className="ml-1" /></Button></Link>
                <Link to="/courses"><Button size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#6b47dc] font-bold h-12 px-8">Browse courses</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
